import { useState, useEffect } from 'react';
import ModalOverlay from '../../ui/ModalOverlay/ModalOverlay';
import s from './DocumentSelector.module.css';
import api from '../../../services/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFile, faCheck } from '@fortawesome/free-solid-svg-icons';

interface Document {
  DocumentID: string;
  FileName: string;
  DocumentType: string;
  S3Key: string;
  S3Bucket: string;
  GeneratedAt: string;
}

interface DocumentSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  caseRef: string;
  onSelect: (docs: Document[]) => void;
}

export default function DocumentSelector({
  isOpen,
  onClose,
  caseRef,
  onSelect,
}: DocumentSelectorProps) {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && caseRef) {
      const fetchDocs = async () => {
        setLoading(true);
        try {
          const res = await api.get(`/api/admin/documents?caseRef=${caseRef}&limit=100`);
          if (res.data.success) {
            setDocuments(res.data.items || res.data.documents || []);
          }
        } catch (err) {
          console.error('Failed to fetch documents', err);
        } finally {
          setLoading(false);
        }
      };
      fetchDocs();
    }
  }, [isOpen, caseRef]);

  const toggleSelect = (doc: Document) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(doc.DocumentID)) {
      newSelected.delete(doc.DocumentID);
    } else {
      newSelected.add(doc.DocumentID);
    }
    setSelectedIds(newSelected);
  };

  const handleConfirm = () => {
    const selectedDocs = documents.filter((d) => selectedIds.has(d.DocumentID));
    onSelect(selectedDocs);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay title="Select Case Documents" onClose={onClose}>
      <div className={s.container}>
        <div className={s.list}>
          {loading ? (
            <div className={s.loading}>Loading documents...</div>
          ) : documents.length === 0 ? (
            <div className={s.empty}>No documents found for this case.</div>
          ) : (
            documents.map((doc) => (
              <div
                key={doc.DocumentID}
                className={`${s.docItem} ${selectedIds.has(doc.DocumentID) ? s.selected : ''}`}
                onClick={() => toggleSelect(doc)}
              >
                <div className={s.docIcon}>
                  <FontAwesomeIcon icon={faFile} />
                </div>
                <div className={s.docInfo}>
                  <div className={s.fileName}>{doc.FileName}</div>
                  <div className={s.meta}>
                    {doc.DocumentType} • {new Date(doc.GeneratedAt).toLocaleDateString()}
                  </div>
                </div>
                {selectedIds.has(doc.DocumentID) && (
                  <div className={s.checkMark}>
                    <FontAwesomeIcon icon={faCheck} />
                  </div>
                )}
              </div>
            ))
          )}
        </div>
        <div className={s.footer}>
          <button className={s.btnSecondary} onClick={onClose}>
            Cancel
          </button>
          <button
            className={s.btnPrimary}
            onClick={handleConfirm}
            disabled={selectedIds.size === 0}
          >
            Attach Selected ({selectedIds.size})
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}
